import React, { useEffect, useState } from "react";
import AgendaNavbar from "../components/AgendaNavbar";
import { MdDelete, MdDownload } from "react-icons/md";
import { FaFileSignature } from "react-icons/fa6";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  deleteOsservazione,
  downloadPdfOsservazione,
  fetchAgenda,
} from "../redux/slices/agendaSlice";
import Loading from "../components/utils/Loading";
import { addNotification } from "../redux/slices/notificationSlice";
import { ActivityOpinion, MessageType } from "../types";
import { hidePopup, showPopup } from "../redux/slices/popupSlice";
import { usePopup } from "../context/PopupContext";

function Storico() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { agenda, loading, error } = useAppSelector(
    (state) => state.agenda
  );
  const { registerCallback } = usePopup();

  useEffect(() => {
    if (id) {
      dispatch(fetchAgenda({ id: Number(id) }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (
      error &&
      !error.includes("Osservazione") &&
      !error.includes("non trovata")
    ) {
      dispatch(addNotification({ message: error, type: MessageType.ERROR }));
    }
  }, [error]);

  const handleDeleteConfirmClick = (anno: string, num: number) => {
    const callbackId = "confirmDelete";
    registerCallback(callbackId, () => handleDelete(anno, num));

    dispatch(
      showPopup({
        type: MessageType.ERROR,
        message:
          `Sei sicuro di voler eliminare l'osservazione #${num.toString().padStart(2, '0')} del ${anno}? I dati non saranno recuperabili`,
        onConfirmId: callbackId,
      })
    );
  };

  const handleDelete = (anno: string, num: number) => {
    if (id && anno && num) {
      dispatch(deleteOsservazione({ id: Number(id), anno, num }))
        .then((result) => {
          if (deleteOsservazione.fulfilled.match(result)) {
            dispatch(
              addNotification({
                message: "Osservazione eliminata",
                type: MessageType.SUCCESS,
              })
            );
            dispatch(hidePopup());
          }
        })
        .catch((error) =>
          dispatch(
            addNotification({
              message: `Errore durante l'eliminazione dell'osservazione: ${error}`,
              type: MessageType.ERROR,
            })
          )
        );
    }
  };

  const handleDownloadPdfOsservazione = async (anno: string, num: number) => {
    if (id && anno && num) {
      try {
        const blob = await downloadPdfOsservazione({
          id: Number(id),
          anno,
          num,
        });
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = `osservazione_${anno}_${num.toString().padStart(2, '0')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Errore durante il download del PDF:", error);
      }
    }
  };

  return (
    <div className="container">      
      <AgendaNavbar />

      {loading && <Loading />}

      {!loading &&
        !error &&
        agenda.map((osservazione, index) => (
          <section
            key={index}
            className={`bg-white border border-gray-300 rounded-2xl ${
              index === 0 ? "rounded-tl-none" : "-mt-2"
            } shadow-md p-6 pb-8 relative z-[99]`}
          >
            <div className="flex items-center justify-between">
              <h3 className="h3">
                Anno Scolastico {osservazione.Anno} #{osservazione.Num.toString().padStart(2, "0")}
              </h3>
              <div className="flex justify-center gap-2">
                <button>
                  <Link
                    className="btn btn-sm btn-primary"
                    to={`/osservazione/${id}/${osservazione.Anno}/${osservazione.Num}`}
                  >
                    <FaFileSignature />
                  </Link>
                </button>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() =>
                    handleDownloadPdfOsservazione(osservazione.Anno, osservazione.Num)
                  }
                >
                  <MdDownload />
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() =>
                    handleDeleteConfirmClick(osservazione.Anno, osservazione.Num)
                  }
                >
                  <MdDelete />
                </button>
              </div>
            </div>

            <table className="mt-2">
              <thead>
                <tr>
                  <th>Attività</th>
                  <th>{ActivityOpinion.POSITIVE}</th>
                  <th>{ActivityOpinion.MIDDLE}</th>
                  <th>{ActivityOpinion.NEGATIVE}</th>
                </tr>
              </thead>
              <tbody>
                {osservazione.Schede.map((scheda, index) => (
                  <tr key={index}>
                    <td>{scheda.Attivita.Nome}</td>
                    <td>
                      {scheda.Giudizio === ActivityOpinion.POSITIVE && "🔵"}
                      {scheda.Giudizio === null && "❓"}
                    </td>
                    <td>
                      {scheda.Giudizio === ActivityOpinion.MIDDLE && "🔵"}
                      {scheda.Giudizio === null && "❓"}
                    </td>
                    <td>
                      {scheda.Giudizio === ActivityOpinion.NEGATIVE && "🔵"}
                      {scheda.Giudizio === null && "❓"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}

      {!loading && !error && agenda.length === 0 && (
        <section
          className={`bg-white border border-gray-300 rounded-2xl rounded-tl-none shadow-md p-6 pb-8 relative z-[99]`}
        >
          <p className="italic text-gray-600">Nessun risultato trovato...</p>
        </section>
      )}
    </div>
  );
}

export default Storico;
